"use client";

/**
 * TooltipManager — single client-side controller that renders portal-based
 * tooltips for any element in the DOM with a `data-tip` attribute.
 *
 * Why a portal (and not CSS ::before/::after pseudo-elements):
 *   The canvas viewer in particular has many ancestors with
 *   `overflow: hidden` (collapsed rails, the canvas stage, the topbar).
 *   A pseudo-element tooltip is positioned inside the trigger and gets
 *   clipped by those ancestors. A portaled tooltip lives in document.body,
 *   outside every ancestor's clipping context, so it can never be clipped.
 *
 * Why a single delegated manager (and not a per-trigger React wrapper):
 *   There are ~50 existing `data-tip="..."` call sites across the app.
 *   This component keeps all of them working without JSX changes — every
 *   existing trigger just needs `data-tip="..."` (and an `aria-label`
 *   for screen readers, which is already in place). Optional
 *   `data-tip-align="end"` flips alignment to the trigger's right edge;
 *   optional `data-tip-position="top"` places the bubble above the
 *   trigger.
 *
 * Accessibility:
 *   The tooltip element is rendered with `role="tooltip"`. The trigger's
 *   existing `aria-label` provides the accessible name; the tooltip is
 *   visual only. Hidden from the AT tree intentionally — duplicating the
 *   aria-label as a tooltip would cause double-announcement on screen
 *   readers.
 */

import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * Render the lines of one tooltip segment with a small opt-in syntax:
 *
 *   `## Heading`        → small uppercase section heading
 *   `# Big line`        → prominent value line (e.g. "68 / 100 — Poor")
 *   `Label | Value`     → two-column row, label left, value right
 *   `> Label | Value`   → highlighted row (e.g. the active grade band)
 *
 * The syntax only activates for content that contains a `## ` heading,
 * so the ~50 plain-text `data-tip` call sites are untouched. Plain
 * lines between structured ones are re-joined with `\n` and rely on
 * the bubble's `white-space: pre-line` for their breaks.
 */
function renderTooltipLines(text: string): ReactNode {
  if (!text.includes("## ")) return text;
  const lines = text.split("\n");
  const nodes: ReactNode[] = [];
  let buffer: string[] = [];
  const flush = (key: string) => {
    if (buffer.length > 0 && buffer.join("").trim() !== "") {
      nodes.push(
        <span key={key} className="ds-tooltip-text">
          {buffer.join("\n")}
        </span>,
      );
    }
    buffer = [];
  };
  lines.forEach((line, i) => {
    if (line.startsWith("## ")) {
      flush(`t${i}`);
      nodes.push(
        <span key={`h${i}`} className="ds-tooltip-heading">
          {line.slice(3)}
        </span>,
      );
    } else if (line.startsWith("# ")) {
      flush(`t${i}`);
      nodes.push(
        <span key={`b${i}`} className="ds-tooltip-big">
          {line.slice(2)}
        </span>,
      );
    } else if (line.includes(" | ")) {
      flush(`t${i}`);
      const active = line.startsWith("> ");
      const [left, right] = (active ? line.slice(2) : line).split(" | ");
      nodes.push(
        <span
          key={`r${i}`}
          className={`ds-tooltip-row${active ? " is-active" : ""}`}
        >
          <span>{left}</span>
          <span className="ds-tooltip-row-val">{right}</span>
        </span>,
      );
    } else {
      buffer.push(line);
    }
  });
  flush("tail");
  return nodes;
}

/**
 * Render tooltip text, turning a line of three-or-more hyphens (`---`,
 * surrounded by blank lines) into a true full-width rule, and lines
 * starting with `## ` into section headings. Plain text is returned
 * untouched, so the ~50 existing `data-tip` call sites are
 * unaffected — only content that opts in with a `---` divider or a
 * `## ` heading changes. The rule bleeds into the bubble's horizontal
 * padding (see `.ds-tooltip-rule`) so it spans edge to edge.
 */
function renderTooltipContent(content: string): ReactNode {
  const parts = content.split(/\n+-{3,}\n+/);
  if (parts.length === 1) return renderTooltipLines(content);
  return parts.map((part, i) => (
    <Fragment key={i}>
      {i > 0 ? <hr className="ds-tooltip-rule" /> : null}
      <span className="ds-tooltip-seg">{renderTooltipLines(part.trim())}</span>
    </Fragment>
  ));
}

type TooltipAlign = "start" | "end";
type TooltipPosition = "top" | "bottom";

interface TooltipState {
  /** Stable id so React remounts the bubble per show (and the layout
   *  effect re-runs to re-measure). */
  key: number;
  content: string;
  align: TooltipAlign;
  position: TooltipPosition;
  /** The trigger that spawned this tooltip — kept on state so the
   *  post-mount measurement can use its bounding rect to point the
   *  arrow at its centre. */
  triggerRect: DOMRect;
  style: CSSProperties;
}

const SHOW_DELAY_MS = 80;
const GAP_PX = 10;
/** Keep the bubble at least this far from any viewport edge after
 *  collision-shifting. Has to be small enough that mid-edge buttons
 *  don't get their arrow pulled noticeably away from their centre,
 *  but large enough that the bubble doesn't visually kiss the edge. */
const VIEWPORT_MARGIN = 8;

export function TooltipManager() {
  const [state, setState] = useState<TooltipState | null>(null);

  /** Ref to the rendered bubble so the layout effect can measure it. */
  const bubbleRef = useRef<HTMLDivElement>(null);
  /** Monotonic counter that drives the bubble's `key` — forces a remount
   *  per show so the layout effect re-runs even when state shape is
   *  similar between shows. */
  const showCounterRef = useRef(0);
  /** The element currently being hovered/focused, so we can correctly
   *  detect mouseleave-equivalent events when moving between child
   *  elements inside the trigger. */
  const activeRef = useRef<HTMLElement | null>(null);
  /** Pending show timer — let the user breeze past elements without
   *  every one flashing a tooltip. Same 60–80ms feel as the old CSS. */
  const showTimerRef = useRef<number | null>(null);

  /**
   * After mount, measure the rendered bubble. If it overflows the
   * viewport in any direction, shift it inward so it stays fully
   * readable, and move the arrow with a CSS variable so it continues
   * to point at the trigger's centre.
   *
   * This is the crucial bit that the portal alone doesn't give us:
   * `position: fixed` escapes ancestor `overflow: hidden`, but the
   * VIEWPORT itself still clips fixed elements when they extend past
   * its edges. That's exactly what was happening with the Share button
   * — its tooltip wanted 280px of width to the right of a trigger that
   * was already near the toolbar's right side, and the rightmost
   * characters were being cut off by the window edge.
   */
  useLayoutEffect(() => {
    if (!state) return;
    const el = bubbleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let dx = 0;
    let dy = 0;
    if (rect.right > vw - VIEWPORT_MARGIN) {
      dx = vw - VIEWPORT_MARGIN - rect.right;
    }
    if (rect.left + dx < VIEWPORT_MARGIN) {
      dx = VIEWPORT_MARGIN - rect.left;
    }
    if (rect.bottom > vh - VIEWPORT_MARGIN) {
      dy = vh - VIEWPORT_MARGIN - rect.bottom;
    }
    if (rect.top + dy < VIEWPORT_MARGIN) {
      dy = VIEWPORT_MARGIN - rect.top;
    }

    if (dx !== 0 || dy !== 0) {
      const currentLeft = parseFloat(el.style.left) || 0;
      const currentTop = parseFloat(el.style.top) || 0;
      el.style.left = `${currentLeft + dx}px`;
      el.style.top = `${currentTop + dy}px`;
    }

    // Reposition the arrow so it still points at the trigger's centre
    // after any shift. The CSS arrow rules use `--ds-tooltip-arrow-x`
    // (with a sensible default) for horizontal placement on top/bottom
    // tooltips, and `--ds-tooltip-arrow-y` for left/right ones in the
    // future. Clamp so the arrow stays inside the bubble corners.
    const finalRect = el.getBoundingClientRect();
    const triggerCx = state.triggerRect.left + state.triggerRect.width / 2;
    const triggerCy = state.triggerRect.top + state.triggerRect.height / 2;
    const arrowX = Math.max(
      14,
      Math.min(finalRect.width - 14, triggerCx - finalRect.left),
    );
    const arrowY = Math.max(
      14,
      Math.min(finalRect.height - 14, triggerCy - finalRect.top),
    );
    el.style.setProperty("--ds-tooltip-arrow-x", `${arrowX}px`);
    el.style.setProperty("--ds-tooltip-arrow-y", `${arrowY}px`);
  }, [state]);

  useEffect(() => {

    function computeStyle(trigger: HTMLElement): TooltipState {
      const rect = trigger.getBoundingClientRect();
      const align = (trigger.getAttribute("data-tip-align") || "start") as TooltipAlign;
      const position = (trigger.getAttribute("data-tip-position") || "bottom") as TooltipPosition;

      // Horizontal anchor: align "start" pins the tooltip's LEFT to the
      // trigger's left edge; "end" pins its RIGHT to the trigger's right
      // edge (handled in CSS via translateX(-100%) on the bubble itself).
      const left = align === "end" ? rect.right : rect.left;

      // Vertical: default below the trigger; "top" places above (handled
      // in CSS via translateY(-100%)).
      const top = position === "top" ? rect.top - GAP_PX : rect.bottom + GAP_PX;

      showCounterRef.current += 1;
      return {
        key: showCounterRef.current,
        content: trigger.getAttribute("data-tip") || "",
        align,
        position,
        triggerRect: rect,
        style: {
          position: "fixed",
          top: `${top}px`,
          left: `${left}px`,
        },
      };
    }

    function show(trigger: HTMLElement) {
      const content = trigger.getAttribute("data-tip");
      if (!content) return;
      setState(computeStyle(trigger));
    }

    function hide() {
      if (showTimerRef.current !== null) {
        window.clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      activeRef.current = null;
      setState(null);
    }

    function scheduleShow(trigger: HTMLElement) {
      if (showTimerRef.current !== null) {
        window.clearTimeout(showTimerRef.current);
      }
      showTimerRef.current = window.setTimeout(() => {
        showTimerRef.current = null;
        // Re-check the trigger is still the active one before showing —
        // the user may have moved on.
        if (activeRef.current === trigger) show(trigger);
      }, SHOW_DELAY_MS);
    }

    function findTrigger(node: EventTarget | null): HTMLElement | null {
      if (!(node instanceof Element)) return null;
      const t = node.closest("[data-tip]");
      return t instanceof HTMLElement ? t : null;
    }

    function onPointerOver(e: PointerEvent) {
      const trigger = findTrigger(e.target);
      if (!trigger || trigger === activeRef.current) return;
      activeRef.current = trigger;
      scheduleShow(trigger);
    }

    function onPointerOut(e: PointerEvent) {
      const trigger = activeRef.current;
      if (!trigger) return;
      // Only treat this as "leaving the trigger" if the new pointer
      // target is OUTSIDE the trigger element (mouseleave semantics).
      const related = e.relatedTarget;
      if (related instanceof Node && trigger.contains(related)) return;
      hide();
    }

    function onFocusIn(e: FocusEvent) {
      const trigger = findTrigger(e.target);
      if (!trigger) return;
      activeRef.current = trigger;
      // Focus path skips the delay — the user has clearly intended to
      // land on this element.
      show(trigger);
    }

    function onFocusOut(e: FocusEvent) {
      const trigger = activeRef.current;
      if (!trigger) return;
      if (e.target instanceof Node && trigger.contains(e.target)) {
        hide();
      }
    }

    function onScroll() {
      // Position is computed from getBoundingClientRect at show time,
      // so any scroll moves the trigger relative to the fixed tooltip.
      // Simplest correct behaviour: hide on scroll.
      if (activeRef.current) hide();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && activeRef.current) hide();
    }

    // Pointer events cover mouse, touch, and pen — single handler set.
    document.addEventListener("pointerover", onPointerOver, true);
    document.addEventListener("pointerout", onPointerOut, true);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("resize", onScroll);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("pointerover", onPointerOver, true);
      document.removeEventListener("pointerout", onPointerOut, true);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("keydown", onKeyDown);
      if (showTimerRef.current !== null) window.clearTimeout(showTimerRef.current);
    };
  }, []);

  if (!state || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      key={state.key}
      ref={bubbleRef}
      role="tooltip"
      // `notranslate` + translate="no": this bubble is portaled into
      // document.body and remounts on every hover. Page-translation
      // extensions (Google Translate et al.) rewrap text nodes in <font>
      // tags; when React then unmounts the bubble its removeChild call
      // targets a node the extension already detached, throwing
      // "Failed to execute 'removeChild' on 'Node'". Opting this dynamic,
      // visual-only node out of translation removes that race. Purely
      // additive — no styling or behaviour change.
      className="ds-tooltip notranslate"
      translate="no"
      data-align={state.align}
      data-position={state.position}
      style={state.style}
      // Tooltip is purely visual; aria-label on the trigger handles the
      // accessible name. Hiding from AT prevents double-announcement.
      aria-hidden="true"
    >
      {renderTooltipContent(state.content)}
    </div>,
    document.body,
  );
}
